import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { NavigationService } from '@core/navigation/navigation.service';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { catchError, Observable, throwError, of, switchMap, tap } from 'rxjs';

export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const navigationService = inject(NavigationService);
    const _confirm = inject(FuseConfirmationService);

    let newReq = req.clone();

    if (authService.accessToken && AuthUtils.isValidJwt(authService.accessToken) && !AuthUtils.isTokenExpired(authService.accessToken)) {
        newReq = req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + authService.accessToken),
        });
        if (navigationService._navigationMenuData.length === 0) {
            navigationService.getMenus();
        }
    }

    const mostrarDialogoError = (error: HttpErrorResponse): Observable<void> => {
        return new Observable<void>((observer) => {
            const descripcion = error.error?.result?.descripcion || 'Ocurrió un error desconocido.';
            const errores = error.error?.result?.errores || {};

            let mensajeError = `${error.message}\n${descripcion}`;
            for (const [campo, mensajes] of Object.entries(errores)) {
                if (Array.isArray(mensajes)) {
                    mensajeError += `\n${campo}: ${mensajes.join(', ')}`;
                }
            }

            _confirm.open({
                title: error.name + ' ' + error.status || 'Error',
                message: mensajeError,
                dismissible: false,
                actions: {
                    confirm: {
                        show: true,
                        color: 'warn',
                        label: 'Confirmar'
                    },
                    cancel: {
                        show: false
                    }
                }
            }).afterClosed().subscribe((result) => {
                observer.next();
                observer.complete();
            });
        });
    };

    return next(newReq).pipe(
        catchError((error) => {
            // Excluir errores 400: no mostrar diálogo, solo propagar error directamente
            if (error.status === 400) {
                return throwError(() => error);
            }

            if (error.status === 0) {
                return mostrarDialogoError(error).pipe(
                    switchMap(() => throwError(() => error))
                );
            }

            if (error.status === 500) {
                if (error.error.code == 23000) {
                    return throwError(() => new Error('No puede eliminar un item que tiene elementos asociados.'));
                }
                return mostrarDialogoError(error).pipe(
                    switchMap(() => throwError(() => error))
                );
            }

            if (error instanceof HttpErrorResponse && error.status === 401) {
                return mostrarDialogoError(error).pipe(
                    tap(() => {
                        authService.signOut();
                        location.reload();
                    }),
                    switchMap(() => throwError(() => error))
                );
            }

            if (error instanceof HttpErrorResponse && error.status === 409) {
                return throwError(() => error);
            }

            // Otros errores
            return mostrarDialogoError(error).pipe(
                switchMap(() => throwError(() => error))
            );
        }),
    );
};